import React, { useState, useEffect, useRef } from "react";
import css from "./FileUpload.module.css";
import { ReactComponent as UploadIcon } from "../../../Assets/svg/upload.svg";
import { ReactComponent as UploadIcon2 } from "../../../Assets/svg/upload2.svg";
import Svg from "../../../Components/Svg/Svg";
import SvgOptions from "../../../Components/Svg/SvgOptions";
import { UploadFile, DownloadFile } from "../../../Services/Server";
import { toast } from "react-toastify";

const FileUpload = ({
  children,
  onChange,
  maxImageSize = 10 * 1024 * 1024, // 10MB
  maxVideoSize = 100 * 1024 * 1024, // 100MB
}) => {
  const { label, name, required, description, value = [], fileTypes = "both" } =
    children || {};
  const [loading, setLoading] = useState(false);
  const [previews, setPreviews] = useState([]);
  const fileInputRef = useRef(null);


  const acceptMap = {
    image: "image/*",
    video: "video/*",
    both: "image/*,video/*",
  };

  // Extension whitelist
  const allowedExts = {
    image: ["png", "gif", "jpg", "jpeg", "jfif", "pjpeg", "pjp", "webp"],
    video: ["mp4", "mov", "avi", "mkv", "webm"],
  };

  const isValidFile = (filename) => {
    if (!filename) return false;
    const ext = filename.split(".").pop().toLowerCase();
    if (fileTypes === "image") return allowedExts.image.includes(ext);
    if (fileTypes === "video") return allowedExts.video.includes(ext);
    return (
      allowedExts.image.includes(ext) || allowedExts.video.includes(ext)
    );
  };

   useEffect(() => {
    if (!value || value.length === 0) {
      setPreviews([]);
      return;
    }

    setLoading(true);

    Promise.all(
      value.map((id) =>
        DownloadFile(id, true)
          .then((blob) => {
            const mimeType = blob.type || "";
            let previewUrl;

            if (mimeType.startsWith("image/")) {
              return new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () =>
                  resolve({ id, url: reader.result, type: mimeType });
                reader.readAsDataURL(blob);
              });
            } else if (mimeType.startsWith("video/")) {
              previewUrl = URL.createObjectURL(blob);
              return { id, url: previewUrl, type: mimeType };
            }
            return null;
          })
          .catch((err) => {
            console.error("Failed to download file:", err);
            return null;
          })
      )
    )
      .then((results) => {
        setPreviews(results.filter(Boolean));
      })
      .catch((err) => {
        console.error(err);
        toast.error("Failed to load existing files.");
      })
      .finally(() => setLoading(false));
  }, [value]);

  
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    for (let file of files) {
      if (!isValidFile(file.name)) {
        toast.error(`"${file.name}" is not a valid ${fileTypes} file.`);
        return;
      }
      if (file.type.startsWith("image/") && file.size > maxImageSize) {
        toast.error(
          `Image "${file.name}" exceeds ${maxImageSize / 1024 / 1024}MB limit.`
        );
        return;
      }
      if (file.type.startsWith("video/") && file.size > maxVideoSize) {
        toast.error(
          `Video "${file.name}" exceeds ${maxVideoSize / 1024 / 1024}MB limit.`
        );
        return;
      }
    }

    setLoading(true);

    Promise.all(
      files.map((file) =>
        UploadFile(file, file.name, true)
          .then((resp) => {
            const newDocId = resp?.id;
            let previewUrl;

            if (file.type.startsWith("image/")) {
              return new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () =>
                  resolve({
                    id: newDocId,
                    url: reader.result,
                    type: file.type,
                  });
                reader.readAsDataURL(file);
              });
            } else if (file.type.startsWith("video/")) {
              previewUrl = URL.createObjectURL(file);
              return { id: newDocId, url: previewUrl, type: file.type };
            }
            return null;
          })
          .catch((err) => {
            console.error("Upload failed:", err);
            toast.error("File upload failed.");
            return null;
          })
      )
    )
      .then((results) => {
        const validResults = results.filter(Boolean);
        const updatedIds = [...(value || []), ...validResults.map((r) => r.id)];
        const updatedPreviews = [...previews, ...validResults];

        setPreviews(updatedPreviews);
        if (children) children.value = updatedIds;
        onChange?.({ target: { name, value: updatedIds } });
      })
      .finally(() => setLoading(false));
  };

  
  const handleRemove = (index) => {
    const updatedPreviews = previews.filter((_, idx) => idx !== index);
    const updatedIds = value.filter((_, idx) => idx !== index);

    setPreviews(updatedPreviews);
    if (children) children.value = updatedIds;
    onChange?.({ target: { name, value: updatedIds } });
  };

  return (
    <div className={css.FileUpload}>
      <div className={css.UploadBox}>
        {loading ? (
          <p className={css.LoadingText}>Loading…</p>
        ) : (
          <>
            {previews.length > 0 ? (
              <div className={css.PreviewList}>
                {previews.map((file, idx) => (
                  <div key={idx} className={css.PreviewWrapper}>
                    {file.type.startsWith("image/") ? (
                      <img
                        src={file.url}
                        alt="Uploaded"
                        className={css.Thumbnail}
                      />
                    ) : (
                      <div className={css.VideoThumbnail}>
                        <video src={file.url} className={css.Video} />
                        <span className={css.PlayOverlay}>▶</span>
                      </div>
                    )}
                    <button
                      type="button"
                      className={css.RemoveButton}
                      onClick={() => handleRemove(idx)}
                    >
                      <Svg className={css.RemoveIcon}>{SvgOptions.Close}</Svg>
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <>
                <UploadIcon className={css.UploadIcon} />
                {label && <span className={css.UploadLabel}>{label}</span>}
                {description && (
                  <p className={css.UploadDescription}>{description}</p>
                )}
              </>
            )}

            <button
              type="button"
              className={css.UploadButton}
              onClick={() => fileInputRef.current.click()}
            >
              <UploadIcon2 className={css.UploadIcon2} />
              Select Files
            </button>
            <input
              ref={fileInputRef}
              type="file"
              name={name}
              multiple
              onChange={handleFileChange}
              className={css.HiddenInput}
              accept={acceptMap[fileTypes]}
              required={required}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default FileUpload;
